import React, { useState } from 'react';
import { submitLead, CRM_PROJECTS, resolveCRMProjectId } from '../../services/crmService';
import { COUNTRY_CODES, getCountry, validatePhone, buildCrmPhone } from '../../services/countryCodes';
import { useContent } from '../../state/siteContent';
import './LeadForm.css';

const BUDGET_OPTS = [
  { label: 'Under ₹20L',      min: '',      max: '20L'    },
  { label: '₹20L – ₹40L',    min: '20L',   max: '40L'    },
  { label: '₹40L – ₹80L',    min: '40L',   max: '80L'    },
  { label: '₹80L – ₹1.5Cr',  min: '80L',   max: '1.5Cr'  },
  { label: '₹1.5Cr – ₹3Cr',  min: '1.5Cr', max: '3Cr'    },
  { label: '₹3Cr+',           min: '3Cr',   max: ''       },
];

/**
 * LeadForm
 *
 * Props:
 *   defaultProjectId    {number}   – pre-select a project (1–4)
 *   defaultProjectSlug  {string}   – pre-select by route slug instead. Takes
 *                                    priority over defaultProjectId — needed
 *                                    because Seaview Enclave and "Talk to
 *                                    experts" currently share CRM id 4 (see
 *                                    crmService.js), so the numeric id alone
 *                                    can't tell them apart; the slug can.
 *   compact             {boolean}  – 2-field version for banners / hero
 *   onSuccess           {Function} – called after successful CRM push
 *   submitLabel         {string}   – override button text
 */
export default function LeadForm({
  defaultProjectId = 4,
  defaultProjectSlug,
  compact = false,
  onSuccess,
  submitLabel = 'Request a Callback',
}) {
  const { settings } = useContent();
  const initialProject = defaultProjectSlug
    ? CRM_PROJECTS.find(p => p.slug === defaultProjectSlug)
    : null;
  const [fields, setFields] = useState({
    name:        '',
    country:     'IN',
    phone:       '',
    email:       '',
    projectId:   initialProject?.id ?? defaultProjectId,
    projectSlug: initialProject?.slug ?? null,
    budgetIndex: '',
    message:     '',
  });
  const [errors,   setErrors]   = useState({});
  const [status,   setStatus]   = useState('idle'); // idle | loading | success | error
  const [apiError, setApiError] = useState('');

  function set(key, val) {
    setFields(f => ({ ...f, [key]: val }));
    if (errors[key]) setErrors(e => ({ ...e, [key]: '' }));
  }

  function validate() {
    const e = {};
    if (!fields.name.trim())                                   e.name  = 'Name is required';
    const phoneErr = validatePhone(fields.country, fields.phone);
    if (phoneErr)                                              e.phone = phoneErr;
    if (fields.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email))
                                                               e.email = 'Enter a valid email';
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setStatus('loading');
    setApiError('');

    try {
      const budget = fields.budgetIndex !== '' ? BUDGET_OPTS[Number(fields.budgetIndex)] : null;
      const countryNote = fields.country !== 'IN'
        ? `[Country: ${getCountry(fields.country).label}] `
        : '';
      await submitLead({
        name:       fields.name,
        phone:      buildCrmPhone(fields.country, fields.phone),
        email:      fields.email || undefined,
        projectId:  Number(fields.projectId),
        minBudget:  budget?.min  || undefined,
        budget:     budget?.max  || undefined,
        remark:     (countryNote + (fields.message || '')) || undefined,
        leadType:   'sales',
      });
      setStatus('success');
      onSuccess?.();
    } catch {
      setStatus('error');
      setApiError('Submission failed. Please try WhatsApp or call us directly.');
    }
  }

  /* ── Success screen ─────────────────────────────── */
  if (status === 'success') {
    return (
      <div className="lf-success">
        <span className="lf-success__star" aria-hidden="true">✦</span>
        <h3 className="lf-success__title">Thank You</h3>
        <p className="lf-success__body">
          Our team will reach out shortly. For an immediate conversation,
          connect with us on WhatsApp.
        </p>
        <a
          className="lf-success__wa"
          href={`https://wa.me/${settings.whatsappRaw}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Connect on WhatsApp
        </a>
      </div>
    );
  }

  /* ── Form ───────────────────────────────────────── */
  return (
    <form className={`lf${compact ? ' lf--compact' : ''}`} onSubmit={handleSubmit} noValidate>

      {/* Row 1 — Name + Phone (always visible) */}
      <div className="lf__row">
        <div className="lf__field">
          <label className="lf__label">Full Name <span className="lf__req">*</span></label>
          <input
            className={`lf__input${errors.name ? ' lf__input--err' : ''}`}
            type="text"
            placeholder="Your full name"
            value={fields.name}
            onChange={e => set('name', e.target.value)}
            autoComplete="name"
          />
          {errors.name && <span className="lf__err-msg">{errors.name}</span>}
        </div>

        <div className="lf__field">
          <label className="lf__label">Mobile Number <span className="lf__req">*</span></label>
          <div className="lf__phone-wrap">
            <select
              className="lf__cc lf__cc--select"
              value={fields.country}
              onChange={e => { set('country', e.target.value); if (errors.phone) setErrors(er => ({ ...er, phone: '' })); }}
              aria-label="Country code"
            >
              {COUNTRY_CODES.map(c => (
                <option key={c.iso} value={c.iso}>
                  {c.code ? `+${c.code}` : '+…'} {c.label}
                </option>
              ))}
            </select>
            <input
              className={`lf__input lf__input--phone${errors.phone ? ' lf__input--err' : ''}`}
              type="tel"
              placeholder={fields.country === 'IN' ? '10-digit number' : 'Mobile number'}
              value={fields.phone}
              maxLength={14}
              onChange={e => set('phone', e.target.value.replace(/\D/g, ''))}
              autoComplete="tel-national"
              inputMode="numeric"
            />
          </div>
          {errors.phone && <span className="lf__err-msg">{errors.phone}</span>}
        </div>
      </div>

      {/* Rows below hidden in compact mode */}
      {!compact && (
        <>
          {/* Row 2 — Email + Project */}
          <div className="lf__row">
            <div className="lf__field">
              <label className="lf__label">Email Address</label>
              <input
                className={`lf__input${errors.email ? ' lf__input--err' : ''}`}
                type="email"
                placeholder="your@email.com"
                value={fields.email}
                onChange={e => set('email', e.target.value)}
                autoComplete="email"
              />
              {errors.email && <span className="lf__err-msg">{errors.email}</span>}
            </div>

            <div className="lf__field">
              <label className="lf__label">Project Interest</label>
              {/* Options are keyed/valued by `slug`, not `id` — Seaview
                  Enclave and "Talk to experts" currently share CRM id 4
                  (Seaview has no real V7Vista project id yet, see
                  crmService.js), and duplicate option values would make a
                  native <select> unable to tell the two apart. `slug` is
                  always unique; fields.projectSlug (set alongside
                  fields.projectId whenever this changes, or by
                  defaultProjectSlug on mount) disambiguates which of the
                  id-4 entries is showing, and falls back to a plain id
                  lookup for any caller that only ever set projectId. */}
              <select
                className="lf__input lf__select"
                value={
                  fields.projectSlug ||
                  CRM_PROJECTS.find(p => p.id === Number(fields.projectId))?.slug ||
                  CRM_PROJECTS[0].slug
                }
                onChange={e => setFields(f => ({
                  ...f,
                  projectId: resolveCRMProjectId(e.target.value),
                  projectSlug: e.target.value,
                }))}
              >
                {CRM_PROJECTS.map(p => (
                  <option key={p.slug} value={p.slug}>{p.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 3 — Budget + Message */}
          <div className="lf__row">
            <div className="lf__field">
              <label className="lf__label">Budget Range</label>
              <select
                className="lf__input lf__select"
                value={fields.budgetIndex}
                onChange={e => set('budgetIndex', e.target.value)}
              >
                <option value="">Select budget (optional)</option>
                {BUDGET_OPTS.map((b, i) => (
                  <option key={i} value={i}>{b.label}</option>
                ))}
              </select>
            </div>

            <div className="lf__field">
              <label className="lf__label">Message</label>
              <input
                className="lf__input"
                type="text"
                placeholder="Any specific requirement?"
                value={fields.message}
                onChange={e => set('message', e.target.value)}
              />
            </div>
          </div>
        </>
      )}

      {status === 'error' && <p className="lf__form-err">{apiError}</p>}

      <button
        type="submit"
        className="lf__submit"
        disabled={status === 'loading'}
      >
        {status === 'loading'
          ? <span className="lf__spinner" aria-label="Submitting…" />
          : submitLabel}
      </button>

      <p className="lf__privacy">
        Your details are kept confidential and never shared with third parties.
      </p>
    </form>
  );
}
